import React, { PureComponent } from "react";
import { withErrorBoundary } from "BaseApp/ErrorBoundary/ErrorBoundary";
import {
  Tabs,
  Tab,
  Paper,
  TextField,
  Select,
  MenuItem,
  withStyles,
  DialogContent,
  DialogTitle,
  AppBar,
  Toolbar,
  IconButton,
  InputLabel,
  Grid,
  Typography,
  FormControl,
  Tooltip,
  InputAdornment
} from "@material-ui/core";
import { Close, HelpOutline } from "@material-ui/icons";
import { sendMessage } from "workerPool";
import { Validator } from "jsonschema";
import DrilldownInput from "components/DrilldownInput";
import { GenericWavefront } from "components/GenericWavefront/GenericWavefront";
import CodeEditor from "./components/CodeEditor/CodeEditor";
import Actions from "./components/Actions/Actions";
import HelperUtils from "MetaCell/helper/HelperUtils";
import DesignTargetHelper from "MetaComponent/helper/DesignTarget";
import Script2DSelector from "MetaComponent/selectors/Script2D";
import Script2DApi from "MetaComponent/api/Script2D";
import { connect } from "react-redux";
import UnselfishDialog from "components/UnselfishDialog/UnselfishDialog";
import Wavefront from "MetaComponent/containers/TargetCanvas/components/Wavefront/Wavefront";
import DirectionSnackbar from "components/Snackbar/Snackbar";

import EnhancedMaterialTable from "components/EnhancedMaterialTable/EnhancedMaterialTable";

import SelectedDesignTargetSelector from "MetaComponent/selectors/SelectedDesignTarget";
import SetPointSelector from "MetaComponent/selectors/SetPoint";

const matrixSchema = {
  type: "array",
  items: {
    type: ["Float64Array", "array"],
    items: {
      type: ["number", "null"]
    }
  }
};

const styles = theme => ({
  dialogTitleBar: {
    padding: 0
  },
  appBar: {
    position: "relative"
  },
  title: {
    flex: 1
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "calc(100vh - 128px)"
  },
  topBarGrid: {
    paddingTop: theme.spacing(2.5),
    alignItems: "flex-end",
    flexShrink: 0
  },
  mainContentGrid: {
    flexGrow: 1,
    minHeight: 0,
    paddingTop: theme.spacing(2.5)
  },
  controlPanelColumn: {
    display: "flex",
    flexDirection: "column",
    height: "100%"
  },
  previewColumn: {
    display: "flex",
    flexDirection: "column",
    height: "100%"
  },
  parameterTabContent: {
    padding: theme.spacing(4),
    overflowY: "auto",
    flexGrow: 1
  },
  codeTabContent: {
    marginTop: theme.spacing(3),
    padding: `0 10px 0 0`
  },
  codeEditorWrapper: {
    position: "relative",
    minHeight: "200px"
  },
  wavefrontPreviewWrapper: {},
  wavefrontPreviewContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start"
  }
});

export class WavefrontGenerator extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: "parameters",
      parameters: [],
      wavefront: null,
      displayedWavefront: null,
      limitedWavefront: null,
      scriptRunning: false,
      error: null,
      disableSave: false,
      fromSample: false,
      selectedSampleId: 0,
      lastScriptRun: "",
      snackbar: {
        message: "",
        visible: false
      },
      code: props.script2Ds.byId[props.scriptId].script
    };

    this.actionCodeUpdater = () => {};
    this.idCounter = 0;
    // Generates unique keys for parameters in the panel.
    this.getUniqueId = () => `param_${this.idCounter++}`;
    // Store the line offset for error reporting
    this.lastRunHeaderOffset = 0;
  }

  componentDidMount() {
    this.initializeStateFromProps(this.props);
  }

  initializeStateFromProps = props => {
    const { script2Ds, scriptId } = props;
    const scriptObject = script2Ds.byId[scriptId];
    let newState = {};

    if (scriptObject) {
      const hasParams =
        Array.isArray(scriptObject.parameters) &&
        scriptObject.parameters.length > 0;
      const hasCode = scriptObject.script && scriptObject.script.trim() !== "";
      newState = {
        code: scriptObject.script,
        parameters: hasParams
          ? scriptObject.parameters.map(p => ({
              ...p,
              value: String(p.value ?? ""),
              id: this.getUniqueId(),
              error: false,
              helperText: ""
            }))
          : [],
        fromSample: hasParams,
        selectedSampleId: hasParams ? scriptObject.id : 0,
        activeTab: hasParams || !hasCode ? "parameters" : "code",

        wavefront: null,
        displayedWavefront: null,
        limitedWavefront: null,
        error: null
      };
      this.setState(newState);
    }
  };

  bindActionCodeUpdater = actionCodeUpdater => {
    this.actionCodeUpdater = actionCodeUpdater;
  };

  componentDidUpdate(prevProps) {
    const { scriptId } = this.props;
    const scriptIdChanged = scriptId !== prevProps.scriptId;

    if (scriptIdChanged) {
      this.initializeStateFromProps(this.props);
    }
  }
  handleTabChange = (event, newValue) => {
    this.setState({ activeTab: newValue });
  };

  getWavelengthInfo = () => {
    const {
      selectedDesignTargets,
      setPoints,
      focusedDesignTargetId
    } = this.props;
    let wavelength = null;
    let wavelengthUnit = "nm";

    if (
      selectedDesignTargets?.byId &&
      setPoints?.byId &&
      focusedDesignTargetId
    ) {
      const activeJob = Object.values(selectedDesignTargets.byId).find(
        job => job.design_target === focusedDesignTargetId
      );

      if (activeJob) {
        const setPointId = activeJob.set_point;
        const currentSetPoint = setPoints.byId[setPointId];

        if (currentSetPoint?.incident_light) {
          wavelength = currentSetPoint.incident_light.wavelength;
          wavelengthUnit = currentSetPoint.unit || "nm";
        }
      }
    }
    return { wavelength, wavelengthUnit };
  };

  handleParameterChange = paramId => event => {
    const newParameters = [...this.state.parameters];
    const paramIndex = newParameters.findIndex(p => p.id === paramId);

    if (paramIndex === -1) {
      return;
    }

    const param = newParameters[paramIndex];
    const value = event.target.value;

    const isSelectField = [
      "pol_type",
      "component_to_generate",
      "mode"
    ].includes(param.name);

    let hasError = false;
    let helperText = "";

    if (!isSelectField) {
      const trimmedValue = value.trim();
      if (trimmedValue === "") {
        hasError = true;
        helperText = "This field is required";
      } else {
        const valueForValidation = trimmedValue.replace(",", ".");

        if (param.name === "ZEMAX_binary2_coef_str") {
          const validCharsRegex = /^[0-9.,;\s-eE+]*$/;
          if (!validCharsRegex.test(value)) {
            hasError = true;
            helperText = "Only numbers, commas, and semicolons are allowed.";
          }
        } else if (
          param.type === "number" ||
          (!param.type && isFinite(valueForValidation))
        ) {
          if (isNaN(Number(valueForValidation))) {
            hasError = true;
            helperText = "Must be a valid number.";
          }
        }
      }
    }

    newParameters[paramIndex] = {
      ...param,
      value: value,
      error: hasError,
      helperText: helperText
    };

    this.setState({ parameters: newParameters });
  };

  renderParameterControl = (param, index) => {
    const tooltipAdornment = (
      <InputAdornment position="end">
        <Tooltip title={param.description} placement="right">
          <IconButton size="small" style={{ marginRight: "8px" }}>
            <HelpOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </InputAdornment>
    );

    if (
      param.name === "pol_type" ||
      param.name === "component_to_generate" ||
      param.name === "mode" ||
      param.name === "mask_type"
    ) {
      const options = {
        pol_type: ["Radial", "Azimuthal"],
        component_to_generate: ["TE", "TM"],
        mode: ["Spatial", "Angular"],
        mask_type: ["circular", "full"]
      };
      return (
        <FormControl
          key={param.name}
          fullWidth
          variant="outlined"
          style={{ marginBottom: 32 }}
        >
          <InputLabel>{param.label}</InputLabel>
          <Select
            label={param.label}
            value={param.value}
            onChange={this.handleParameterChange(param.id)}
            endAdornment={tooltipAdornment}
            IconComponent={() => null}
          >
            {options[param.name].map(opt => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <FormControl key={param.name} fullWidth style={{ marginBottom: 32 }}>
        <TextField
          label={param.label}
          variant="outlined"
          value={param.value}
          onChange={this.handleParameterChange(param.id)}
          error={!!param.error}
          helperText={param.error ? param.helperText : ""}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" style={{ marginRight: "-12px" }}>
                {tooltipAdornment}
              </InputAdornment>
            )
          }}
        />
      </FormControl>
    );
  };

  /**
   * a code updater to be passed down because we also want to keep the code in this
   * component. We are not using state because it rerenders the component every time
   * a key is pressed causing a slowness in the wavefront component
   * @callback
   */
  updateCode = newCode => {
    if (newCode !== this.state.code) {
      this.setState({ code: newCode, fromSample: false, selectedSampleId: 0 });
      this.actionCodeUpdater(newCode);
    }
  };

  /**
   * it calls the confirmation callback with the wavefront data and updates the script
   * and closes the dialog
   */
  handleConfirm = () => {
    const { scriptId, onConfirm, updateScript } = this.props;
    const { code, parameters, wavefront } = this.state;

    const scriptDataToUpdate = {
      script: code,
      parameters: parameters.map(({ id, tableData, ...rest }) => rest)
    };

    updateScript(scriptId, scriptDataToUpdate);

    onConfirm(wavefront);
    this.handleClose();
  };

  /**
   * it resets the wavefront and error state and calls the passed close callback
   */
  handleClose = () => {
    this.setState({
      wavefront: null,
      displayedWavefront: null,
      limitedWavefront: null,
      error: null
    });
    this.props.onClose();
  };

  validateMatrix(matrix) {
    const validator = new Validator();
    try {
      return validator.validate(matrix, matrixSchema);
    } catch (exception) {
      return {
        errors: [exception]
      };
    }
  }

  /**
   * it checks whether the matrix is in the correct shape depending on the required types.
   * @param {Number[][]} wavefront - the wavefront data
   * @returns {String} an error message
   */
  validateWavefront(wavefront) {
    const { onlyAmplitude } = this.props,
      amplBadShapeMsg = "Amplitude must be a matrix of valid number values",
      phaseBadShapeMsg = "Phase must be a matrix of valid number values",
      amplitude =
        onlyAmplitude && wavefront.length !== 2 ? wavefront : wavefront[0],
      amplitudeErrors = this.validateMatrix(amplitude).errors;
    if (amplitudeErrors.length) {
      return amplBadShapeMsg;
    }
    if (!onlyAmplitude) {
      const phase = wavefront[1],
        phaseErrors = this.validateMatrix(phase).errors;
      if (phaseErrors.length) {
        return phaseBadShapeMsg;
      }
    }
    return null;
  }

  /**
   * it sets the error message to the state if the worker returns an exception
   * else it validates if the wavefront has the correct matrix shape.
   * in case the data is valid, it is set to the state.
   * @callback
   * @param {*} data - the worker response
   */
  handleScriptResponse = data => {
    if (data instanceof Error) {
      // only the text after the splitter below is informative to the user.
      let rawError = data.message.split(
        /File "<unknown>",|File \"<exec>\",/
      )[1];
      rawError = rawError || data.message;

      let finalError = rawError;

      // Use a regex to find the line number
      const lineMatch = rawError.match(/line (\d+)/);

      if (lineMatch && lineMatch[1]) {
        const originalLine = parseInt(lineMatch[1], 10);
        const correctedLine = originalLine - this.lastRunHeaderOffset;

        if (correctedLine > 0) {
          finalError = rawError.replace(
            `line ${originalLine}`,
            `line ${correctedLine}`
          );
        } else {
          finalError = `Error in generated parameter header: ${rawError}`;
        }
      }

      // Check for non-blocking memory errors to allow saving the script.
      const isMemoryError = finalError.includes("_ArrayMemoryError");

      this.setState({
        error: finalError,
        scriptRunning: false,
        // Only disable the save button if it's a real script error, not a memory error.
        disableSave: !isMemoryError
      });
      return;
    }

    const parsedData = JSON.parse(data);
    const { onlyAmplitude } = this.props;

    const amplitudeError =
      "Amplitude should be of datatype 'float' or NaN, we suspect this issue arises from using complex values and suggest to end with amplitude=np.abs(amplitude) to solve this issue";
    const phaseError =
      "Phase should be of datatype 'float' or NaN, we suspect this issue arises from using complex values and suggest to end with phase=np.abs(phase) to solve this issue";

    const amplitude =
      onlyAmplitude && parsedData.length !== 2 ? parsedData : parsedData[0];
    const phase =
      !onlyAmplitude && parsedData.length === 2 ? parsedData[1] : null;

    const isComplexString = v => typeof v === "string" && v.includes("j");

    let validationError = null;

    // 1. First, check specifically for complex-like strings.
    if (Array.isArray(amplitude) && amplitude.flat().some(isComplexString)) {
      validationError = amplitudeError;
    } else if (
      phase &&
      Array.isArray(phase) &&
      phase.flat().some(isComplexString)
    ) {
      validationError = phaseError;
    } else {
      // 2. If no complex strings are found, run the generic validation.
      validationError = this.validateWavefront(parsedData);
    }

    if (validationError) {
      this.setState({
        error: validationError,
        wavefront: null,
        scriptRunning: false,
        disableSave: true
      });
    } else {
      let newLimitedWavefront;
      if (this.props.onlyAmplitude) {
        const amplitudeData =
          parsedData.length === 2 ? parsedData[0] : parsedData;
        newLimitedWavefront = [
          HelperUtils.limitDecimals(amplitudeData, 4, true)
        ];
      } else {
        newLimitedWavefront = HelperUtils.limitDecimals(parsedData, 4, true);
      }

      this.setState({
        wavefront: parsedData,
        displayedWavefront: parsedData,
        limitedWavefront: newLimitedWavefront,
        error: null,
        scriptRunning: false,
        disableSave: false,
        lastScriptRun: this.state.code
      });
    }
  };

  /**
   * it checks whether the required variables are present in the code
   * @param {String} code - python code
   * @returns {String} an error message;
   */
  validateCode(code) {
    const { onAsyncGenerate } = this.props;
    if (onAsyncGenerate && code.includes("import")) {
      return "additional imports are not allowed";
    }
    if (code.includes("exec(")) {
      return "The 'exec()' function is not allowed for security reasons.";
    }
    if (!code.includes("amplitude=") && !code.includes("amplitude =")) {
      return "amplitude variable is required";
    } else if (
      !this.props.onlyAmplitude &&
      !code.includes("phase=") &&
      !code.includes("phase =")
    ) {
      return "phase variable is required";
    }
    return null;
  }

  /**
   * Creates a string of Python code with parameter assignments prepended.
   * @param { string } code - The user's Python script.
   * @param { Array < Object >} parameters - The array of parameter objects.
   * @returns { String } The full script ready for execution.
   */
  getScriptWithInjectedParameters = (code, parameters) => {
    if (!parameters || parameters.length === 0) {
      return code;
    }
    const header = [];

    parameters.forEach(param => {
      let valueForCode;
      const processedValue = String(param.value)
        .trim()
        .replace(",", ".");

      if (isFinite(processedValue) && processedValue !== "") {
        valueForCode = processedValue;
      } else {
        valueForCode = `'${String(param.value).replace(/'/g, "\\'")}'`;
      }
      header.push(`${param.name} = ${valueForCode}`);
    });

    return header.join("\n") + "\n" + code;
  };

  /**
   * it sends the code to the worker for execution.
   * @callback
   */
  generate = async () => {
    const { code, parameters } = this.state;
    const error = this.validateCode(code);

    if (error) {
      this.setState({ error });
      return;
    }

    const fullScript = this.getScriptWithInjectedParameters(code, parameters);

    // Store the line offset
    this.lastRunHeaderOffset = parameters?.length || 0;

    this.setState({ scriptRunning: true, error: null });
    await sendMessage(
      {
        workerId: "PYODIDE",
        action: "GET_WAVEFRONT",
        params: {
          code: fullScript,
          dimensions: this.props.fixedShape
            ? {
                componentWidth: parseFloat(this.props.componentWidth),
                componentHeight: parseFloat(this.props.componentHeight),
                cellWidth: DesignTargetHelper.getComponentDimensionfromMetacellDim(
                  this.props.cellWidth,
                  this.props.componentUnit,
                  this.props.cellUnit
                ),
                cellHeight: DesignTargetHelper.getComponentDimensionfromMetacellDim(
                  this.props.cellHeight,
                  this.props.componentUnit,
                  this.props.cellUnit
                ),
                nrX: parseFloat(this.props.componentNrX),
                nrY: parseFloat(this.props.componentNrY)
              }
            : undefined
        }
      },
      data => {
        this.handleScriptResponse(data);
      }
    );
  };

  generateServerSide = () => {
    const { onAsyncGenerate, updateScript, scriptId } = this.props;
    // For server-side generation, send the clean code and parameters separately
    const { code, parameters } = this.state;
    const error = this.validateCode(code);
    const { wavelength } = this.getWavelengthInfo();
    const cleanParameters = parameters.map(
      ({ id, tableData, ...rest }) => rest
    );

    if (error) {
      this.setState({ error });
      return;
    }

    this.setState({
      scriptRunning: true,
      error: null,
      snackbar: { message: "", visible: false }
    });

    const asyncPayload = {
      code: code,
      wavelength: wavelength,
      parameters: cleanParameters,
      dimensions: this.props.fixedShape
        ? {
            componentWidth: parseFloat(this.props.componentWidth),
            componentHeight: parseFloat(this.props.componentHeight),
            cellWidth: DesignTargetHelper.getComponentDimensionfromMetacellDim(
              this.props.cellWidth,
              this.props.componentUnit,
              this.props.cellUnit
            ),
            cellHeight: DesignTargetHelper.getComponentDimensionfromMetacellDim(
              this.props.cellHeight,
              this.props.componentUnit,
              this.props.cellUnit
            ),
            nrX: parseFloat(this.props.componentNrX),
            nrY: parseFloat(this.props.componentNrY)
          }
        : undefined
    };

    const scriptDataToUpdate = {
      script: code,
      parameters: cleanParameters
    };

    onAsyncGenerate(
      asyncPayload,
      () => {
        updateScript(scriptId, scriptDataToUpdate);
        this.setState({ scriptRunning: false });
        this.handleClose();
      },
      error_message => {
        this.setState({
          error: error_message,
          snackbar: {
            visible: true,
            message: error_message
          },
          scriptRunning: false
        });
      }
    );
  };

  /**
   * @returns {Object[]} options created from the sample scripts
   */
  getDrilldownOptions() {
    if (!this.props.sampleScripts) return [[]];

    return [
      Object.values(this.props.sampleScripts)
        .filter(sample => sample)
        .map(sample => ({
          text: sample.name,
          isSelected: false
        }))
    ];
  }

  /**
   * it sets the selected sample id in the state
   * it is supposed to be passed as an "on change callback" to the drilldown component.
   * @param {String} selectedOptionIndex - the index of the selected option
   * @callback
   */
  onSampleSelect = selectedOptionIndex => {
    const samplesArray = Object.values(this.props.sampleScripts);
    const { wavelength, wavelengthUnit } = this.getWavelengthInfo();
    const selectedSample = samplesArray[selectedOptionIndex];

    if (selectedSample) {
      let parameters = (selectedSample.parameters || []).map(p => ({
        ...p,
        value: String(p.value ?? ""),
        id: this.getUniqueId(),
        error: false,
        helperText: ""
      }));

      // Check for a wavelength from props to override the sample's default
      if (wavelength !== null && wavelength !== undefined) {
        const wavelengthParamIndex = parameters.findIndex(
          p => p.name === "wavelength"
        );

        if (wavelengthParamIndex > -1) {
          let wavelengthInUm = wavelength;
          if (wavelengthUnit === "nm") {
            wavelengthInUm = wavelength / 1000;
          }
          parameters[wavelengthParamIndex].value = String(wavelengthInUm);
        }
      }

      this.setState({
        selectedSampleId: selectedSample.id,
        code: selectedSample.script,
        parameters: parameters,
        fromSample: true,
        error: null
      });
    }
  };

  /**
   * Renders the parameter editor table.
   * @returns {JSX.Element}
   */
  renderParameterTable = () => {
    const { parameters } = this.state;

    const renderTextField = props => (
      <TextField
        fullWidth
        value={props.value || ""}
        onChange={event => props.onChange(event.target.value)}
      />
    );

    const renderFocusedTextField = props => (
      <TextField
        fullWidth
        value={props.value || ""}
        onChange={event => props.onChange(event.target.value)}
        autoFocus
      />
    );

    return (
      <div style={{ flexShrink: 0 }}>
        <EnhancedMaterialTable
          title="Parameters"
          style={{
            boxShadow: "none",
            border: "1px solid rgba(224, 224, 224, 1)",
            borderRadius: "4px"
          }}
          options={{
            paging: false,
            search: false,
            sorting: false,
            draggable: false,
            addRowPosition: "last",
            actionsColumnIndex: -1,
            maxBodyHeight: "250px",
            toolbar: true
          }}
          columns={[
            {
              title: "Variable Name",
              field: "name",
              editComponent: renderFocusedTextField
            },
            {
              title: "Value",
              field: "value",
              editComponent: renderTextField
            },
            {
              title: "Label",
              field: "label",
              editComponent: renderTextField
            },
            {
              title: "Description",
              field: "description",
              editComponent: renderTextField
            }
          ]}
          data={parameters}
          editable={{
            onRowAdd: newData =>
              new Promise(resolve => {
                if (!newData || !newData.name || newData.name.trim() === "") {
                  resolve();
                  return;
                }

                const processedData = {
                  name: "new_param",
                  value: "0",
                  description: "",
                  ...newData,
                  id: this.getUniqueId()
                };

                processedData.value = String(processedData.value ?? "0");

                if (!processedData.label) {
                  processedData.label = processedData.name;
                }

                this.setState(
                  prevState => ({
                    parameters: [...prevState.parameters, processedData],
                    fromSample: false
                  }),
                  () => resolve()
                );
              }),
            onRowUpdate: (newData, oldData) =>
              new Promise(resolve => {
                const newParameters = [...this.state.parameters];
                const index = newParameters.findIndex(p => p.id === oldData.id);

                if (index > -1) {
                  const updatedData = { ...newData, id: oldData.id };

                  updatedData.value = String(updatedData.value ?? "");

                  if (!updatedData.label) {
                    updatedData.label = updatedData.name;
                  }

                  newParameters[index] = updatedData;

                  this.setState(
                    { parameters: newParameters, fromSample: false },
                    () => resolve()
                  );
                } else {
                  resolve();
                }
              }),
            onRowDelete: oldData =>
              new Promise(resolve => {
                const newParameters = [...this.state.parameters];
                const index = newParameters.findIndex(p => p.id === oldData.id);
                if (index > -1) {
                  newParameters.splice(index, 1);
                  this.setState(
                    { parameters: newParameters, fromSample: false },
                    () => resolve()
                  );
                } else {
                  resolve();
                }
              })
          }}
        />
      </div>
    );
  };

  /**
   * Renders the simplified scripting rules and info text.
   * @returns {JSX.Element}
   */
  renderScriptingRules = () => {
    const {
      fixedShape,
      cellWidth,
      componentNrX,
      cellHeight,
      componentNrY
    } = this.props;

    if (!fixedShape) return null;

    const x_start = -(cellWidth * (componentNrX - 1)) / 2;
    const x_end = (cellWidth * (componentNrX - 1)) / 2;
    const y_start = -(cellHeight * (componentNrY - 1)) / 2;
    const y_end = (cellHeight * (componentNrY - 1)) / 2;

    const codeStyle = {
      fontFamily: "monospace",
      backgroundColor: "rgba(0,0,0,0.05)",
      padding: "2px 4px",
      borderRadius: "3px"
    };

    const rulesTooltipContent = (
      <React.Fragment>
        <ul style={{ paddingLeft: "20px", margin: 0, textAlign: "left" }}>
          <li>Import statements are not allowed.</li>
          <li>Numpy (as np) and scipy are pre-imported.</li>
          <li>Functions must be declared as global.</li>
          <li>Previewing large components may be slow.</li>
        </ul>
      </React.Fragment>
    );

    return (
      <div style={{ marginBottom: "16px", position: "relative" }}>
        <Typography
          variant="body2"
          component="div"
          color="textSecondary"
          paragraph
        >
          The following variables are pre-defined based on the component's
          geometry:
          <pre
            style={{ ...codeStyle, marginTop: "8px", whiteSpace: "pre-wrap" }}
          >
            {`x_coords = np.linspace(${x_start.toFixed(2)}, ${x_end.toFixed(
              2
            )}, ${componentNrX})\ny_coords = np.linspace(${y_start.toFixed(
              2
            )}, ${y_end.toFixed(
              2
            )}, ${componentNrY})\nx, y = np.meshgrid(x_coords, y_coords)`}
          </pre>
        </Typography>

        <Tooltip title={rulesTooltipContent}>
          <IconButton
            size="small"
            style={{ position: "absolute", top: 0, right: 0 }}
          >
            <HelpOutline />
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  /**
   * it renders the control panel with the parameters and code editor.
   * @returns {JSX.Element} the control panel
   */
  renderControlPanel = () => {
    const { activeTab, parameters, code } = this.state;
    const { classes } = this.props;

    return (
      <React.Fragment>
        <Paper>
          <Tabs
            value={activeTab}
            onChange={this.handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Parameters" value="parameters" />
            <Tab label="Code" value="code" />
          </Tabs>
        </Paper>

        {/* --- PARAMETERS TAB CONTENT --- */}
        {activeTab === "parameters" && (
          <div className={classes.parameterTabContent}>
            <React.Fragment>
              {(() => {
                if (!parameters.length) {
                  return (
                    <React.Fragment>
                      <Typography variant="h6" component="h3" gutterBottom>
                        Wavefront Generator
                      </Typography>
                      <Typography variant="body2" paragraph>
                        To begin, choose a template from the{" "}
                        <strong>Samples</strong> dropdown above. Its parameters
                        will appear here for you to view and modify.
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        For complete control, you can also develop a custom
                        wavefront by writing Python directly in the{" "}
                        <strong>Code</strong> tab.
                      </Typography>
                    </React.Fragment>
                  );
                }

                return parameters.map(param => {
                  return this.renderParameterControl(param);
                });
              })()}
            </React.Fragment>
          </div>
        )}

        {activeTab === "code" && (
          <div className={classes.codeTabContent}>
            {this.renderScriptingRules()}
            <div className={classes.codeEditorWrapper}>
              <CodeEditor
                width="100%"
                script={code}
                updateCode={this.updateCode}
              />
            </div>
          </div>
        )}
      </React.Fragment>
    );
  };

  /**
   * it renders the wavefront preview based on the current state.
   * it uses the GenericWavefront component if onlyAmplitude is true, otherwise it uses the Wavefront component.
   * @returns {JSX.Element|null} the wavefront preview or null
   */
  renderWavefrontPreview = () => {
    const { wavefront, limitedWavefront } = this.state;
    const {
      onlyAmplitude,
      wfWidth,
      wfHeight,
      wfUnit,
      componentWidth,
      componentHeight,
      cellWidth,
      cellHeight,
      componentNrX,
      componentNrY,
      classes
    } = this.props;

    // If limitedWavefront doesn't exist, try to create it from the raw wavefront
    const wavefrontForDisplay =
      limitedWavefront ||
      (wavefront ? HelperUtils.limitDecimals(wavefront, 4, true) : null);

    if (!wavefrontForDisplay) return null;

    const width = componentNrX
      ? cellWidth * (componentNrX - 1)
      : wfWidth || componentWidth;
    const height = componentNrY
      ? cellHeight * (componentNrY - 1)
      : wfHeight || componentHeight;

    if (width === 0 || height === 0) {
      return <div style={{ flex: 1, height: "100%" }} />;
    }

    // Case 1: Single plot view. Use a sized/centered wrapper.
    if (onlyAmplitude) {
      return (
        <div className={classes.wavefrontPreviewContainer}>
          <div style={{ width: "70%" }}>
            <GenericWavefront
              hidePhase
              wavefront={wavefrontForDisplay}
              wfWidth={width}
              wfHeight={height}
              unit={wfUnit}
            />
          </div>
        </div>
      );
    }

    // Case 2: Dual plot view. Render Wavefront directly.
    return (
      <div className={classes.wavefrontPreviewContainer}>
        <Wavefront
          wavefront={wavefrontForDisplay}
          wfWidth={width}
          wfHeight={height}
          unit={wfUnit}
          showLegend
        />
      </div>
    );
  };

  /**
   * Renders info about component size at the top of the dialog.
   * @returns {JSX.Element}
   */
  renderTopBarInfo = () => {
    const {
      fixedShape,
      cellWidth,
      componentNrX,
      componentUnit,
      cellHeight,
      componentNrY,
      cellUnit
    } = this.props;

    if (!fixedShape) return null;

    const totalWidth = Number((cellWidth * componentNrX).toFixed(10));
    const totalHeight = Number((cellHeight * componentNrY).toFixed(10));

    return (
      <div style={{ textAlign: "right" }}>
        <Typography
          test-data={"componentSize"}
          variant="subtitle1"
          component="h4"
        >
          {`Meta component size: ${totalWidth}${componentUnit} x ${totalHeight}${componentUnit}`}
        </Typography>
        <Typography test-data={"cellSize"} variant="subtitle1" component="h4">
          {`Meta cell size: ${cellWidth}${cellUnit} x ${cellHeight}${cellUnit}`}
        </Typography>
      </div>
    );
  };

  /**
   * it renders the entire component including the dialog, control panel, and wavefront preview.
   * @returns {JSX.Element} the complete dialog with all components
   */
  render = () => {
    const {
      classes,
      open,
      sampleScripts,
      onAsyncGenerate,
      componentNrX,
      componentNrY
    } = this.props;
    const {
      activeTab,
      scriptRunning,
      error,
      disableSave,
      fromSample,
      selectedSampleId,
      wavefront,
      snackbar,
      parameters,
      code
    } = this.state;

    const hasParameterErrors = parameters.some(p => p.error);
    const isCodeEmpty = !code || code.trim() === "";
    const disableActions = hasParameterErrors || isCodeEmpty;

    const selectedSampleName =
      fromSample && selectedSampleId
        ? sampleScripts.find(s => s.id === selectedSampleId)?.name
        : "";
    const drilldownKey = fromSample ? `sample-${selectedSampleId}` : "custom";
    const totalMetaCells = componentNrX * componentNrY;
    const isLargerMetaCells = totalMetaCells > 4000000;

    const previewWrapperStyle = {
      marginTop: activeTab === "code" ? "16px" : "0"
    };

    // Conditional styling for layout changes
    const isCodeTab = activeTab === "code";

    const dialogContentStyle = isCodeTab
      ? { height: "auto", overflowY: "auto" }
      : {};
    const mainContentGridStyle = isCodeTab ? { flexGrow: "initial" } : {};
    const controlPanelColumnStyle = isCodeTab ? { height: "auto" } : {};
    const previewColumnStyle = isCodeTab ? { height: "auto" } : {};

    return (
      <UnselfishDialog
        open={open}
        onClose={this.handleClose}
        fullScreen={true}
        maxWidth="xl"
      >
        <DialogTitle
          onClose={this.handleClose}
          disableTypography={true}
          className={classes.dialogTitleBar}
        >
          <AppBar className={classes.appBar}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={this.handleClose}
                aria-label="close"
              >
                <Close />
              </IconButton>
            </Toolbar>
          </AppBar>
        </DialogTitle>

        <DialogContent
          className={classes.dialogContent}
          style={dialogContentStyle}
        >
          <Grid container spacing={2} className={classes.topBarGrid}>
            <Grid item xs={5}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  width: "100%"
                }}
              >
                <div>
                  <InputLabel style={{ fontSize: 12 }}>Samples</InputLabel>
                  <DrilldownInput
                    key={drilldownKey}
                    marginTop={0}
                    float={"left"}
                    value={selectedSampleName}
                    options={this.getDrilldownOptions()}
                    onSelect={this.onSampleSelect}
                    showCount={false}
                  />
                </div>
                {this.renderTopBarInfo()}
              </div>
            </Grid>
            <Grid item xs={7} />
          </Grid>

          <Grid
            container
            spacing={2}
            className={classes.mainContentGrid}
            style={mainContentGridStyle}
          >
            <Grid
              item
              xs={5}
              className={classes.controlPanelColumn}
              style={controlPanelColumnStyle}
            >
              {this.renderControlPanel()}
            </Grid>
            <Grid
              item
              xs={7}
              className={classes.previewColumn}
              style={previewColumnStyle}
            >
              {activeTab === "code" && this.renderParameterTable()}
              <div
                className={classes.wavefrontPreviewWrapper}
                style={previewWrapperStyle}
              >
                {this.renderWavefrontPreview()}
              </div>
            </Grid>
          </Grid>
        </DialogContent>

        <Actions
          scriptRunning={scriptRunning}
          error={error}
          disableSave={disableSave}
          disableActions={disableActions}
          wavefront={wavefront}
          handleClose={this.handleClose}
          handleConfirm={this.handleConfirm}
          generate={this.generate}
          isLargerMetaCells={isLargerMetaCells}
          generateAsync={onAsyncGenerate ? this.generateServerSide : null}
          bindActionCodeUpdater={this.bindActionCodeUpdater}
        />

        {snackbar.visible && <DirectionSnackbar message={snackbar.message} />}
      </UnselfishDialog>
    );
  };
}

const mapStateToProps = state => {
  const selectedDesignTargets = SelectedDesignTargetSelector.getSelectedDesignTargets(
    state
  );
  const setPoints = SetPointSelector.getSetPoints(state);
  const script2Ds = Script2DSelector.getScript2Ds(state);

  return {
    script2Ds: script2Ds,
    selectedDesignTargets: selectedDesignTargets,
    setPoints: setPoints
  };
};

const mapDispatchToProps = dispatch => ({
  updateScript: (id, properties) =>
    dispatch(Script2DApi.updateScript2D(id, properties))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorBoundary(withStyles(styles)(WavefrontGenerator)));
